/* eslint-disable @typescript-eslint/no-explicit-any */
import React, { useEffect, useState } from 'react';
import {
  Box,
  Typography,
  TextField,
  Button,
  IconButton,
  CardContent,
  FormControlLabel,
  Checkbox,
  Select,
  MenuItem,
  FormControl as MuiFormControl,
  Stack,
} from '@mui/material';
import { Delete as DeleteIcon, DragIndicator as DragIcon, AddCircleOutline as AddCircleOutlineIcon } from '@mui/icons-material';
import { useSortable } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { LocalizationProvider } from '@mui/x-date-pickers';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import { DemoContainer } from '@mui/x-date-pickers/internals/demo';
import { DateTimePicker, TimePicker } from '@mui/x-date-pickers';
import dayjs from 'dayjs';
import {
  MessageTemplate as MessageTemplateType,
  CampaignConfigType,
} from '../../types';
import { DefaultCustomField } from 'src/constants/types';
import TemplateText from 'src/components/TemplateText/TemplateText';
import { SelectableCard } from './SelectableCard.styles';
import { SquareIconButton } from 'src/UIKit';
import { flushControlLabelSx, pickerRowOffsetSx } from '../../helpers';

interface MessageTemplateProps {
  template: MessageTemplateType;
  campaignType: CampaignConfigType;
  metadataTypes: string[];
  customFields: DefaultCustomField[];
  isSelected: boolean;
  onSelect: () => void;
  onUpdate: (updates: Partial<MessageTemplateType>) => void;
  onDelete: () => void;
  /** Poll configs: survey is mandatory — render it checked + read-only and keep it on. */
  forceSurvey?: boolean;
}

export const MessageTemplate: React.FC<MessageTemplateProps> = ({
  template,
  campaignType,
  metadataTypes,
  customFields,
  isSelected,
  onSelect,
  onUpdate,
  onDelete,
  forceSurvey = false,
}) => {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({
    id: template.id,
    data: {
      type: 'message-template',
    },
  });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
  };

  // Schedule state management
  const [delayValue, setDelayValue] = useState<string>(
    template.schedule?.scheduledType === 'delay' && template.schedule.delay
      ? template.schedule.delay.replace('-', '').slice(0, -1)
      : '',
  );
  const [delayPeriod, setDelayPeriod] = useState<string>('m');

  // Annual-specific schedule state management
  const minutesInDay = 1440;
  const [pastOrFuture, setPastOrFuture] = useState<string>('before');
  const [delayDays, setDelayDays] = useState<number | undefined>(
    template.schedule?.scheduledType === 'delay' && template.schedule.delay
      ? Math.floor(Math.abs(Number(template.schedule.delay.replace('m', ''))) / minutesInDay)
      : 0,
  );
  const [delayTime, setDelayTime] = useState<number>(
    template.schedule?.scheduledType === 'delay' && template.schedule.delay
      ? Math.abs(Number(template.schedule.delay.replace('m', ''))) % minutesInDay
      : 540,
  );

  const handleScheduleTypeChange = (e: any) => {
    const { value } = e.target;
    if (value === 'delay') {
      setDelayValue('');
      setDelayPeriod('m');
      onUpdate({
        schedule: {
          value: template.schedule?.value || 'immediately',
          visible: template.schedule?.visible ?? true,
          scheduledType: value as any,
          delay: undefined,
          time: undefined,
        },
      });
    }
    if (value === 'reply' || value === 'contact_added') {
      setDelayValue('');
      setDelayPeriod('m');
      onUpdate({
        schedule: {
          value: template.schedule?.value || 'immediately',
          visible: template.schedule?.visible ?? true,
          scheduledType: value as any,
          time: undefined,
          delay: undefined,
        },
      });
    }
    if (value === 'time') {
      setDelayValue('');
      setDelayPeriod('m');
      onUpdate({
        schedule: {
          value: template.schedule?.value || 'immediately',
          visible: template.schedule?.visible ?? true,
          scheduledType: value as any,
          delay: undefined,
        },
      });
    }
    if (value === 'immediate') {
      setDelayValue('');
      setDelayPeriod('m');
      onUpdate({
        schedule: {
          value: template.schedule?.value || 'immediately',
          visible: template.schedule?.visible ?? true,
          scheduledType: value as any,
          time: undefined,
          delay: undefined,
        },
      });
    }
  };

  const handleSchedulePeriodChange = ({ target }: any) => {
    setDelayPeriod(target.value);
    onUpdate({
      schedule: {
        value: template.schedule?.value ?? 'immediately',
        visible: template.schedule?.visible ?? true,
        scheduledType: template.schedule?.scheduledType,
        time: undefined,
        delay: `${delayValue}${target.value}`,
      },
    });
  };

  const handleDelayChange = (e: any) => {
    const { value } = e.target;
    setDelayValue(value);
    onUpdate({
      schedule: {
        value: template.schedule?.value ?? 'immediately',
        visible: template.schedule?.visible ?? true,
        scheduledType: template.schedule?.scheduledType,
        time: undefined,
        delay: `${value}${delayPeriod}`,
      },
    });
  };

  const handleDateTimeSave = async (date: dayjs.Dayjs | null) => {
    const isValid = dayjs(date).isValid();
    onUpdate({
      schedule: {
        value: template.schedule?.value ?? 'immediately',
        visible: template.schedule?.visible ?? true,
        scheduledType: template.schedule?.scheduledType,
        delay: undefined,
        time: isValid ? dayjs(date).toDate().toISOString() : undefined,
      },
    });
  };

  // Annual-specific helper functions
  const getDelayInMinutes = (delay: string) => {
    const value = delay.slice(0, -1);
    const delayInMinutes = Math.abs(Number(value));
    return delayInMinutes;
  };

  const isDelayBefore = (delay: string) => {
    return delay.startsWith('-');
  };

  const getDelayDays = (delay: string | undefined): number => {
    if (!delay) {
      return 0;
    }
    const delayInMinutes = getDelayInMinutes(delay);
    const isBefore = isDelayBefore(delay);

    const days = isBefore
      ? Math.ceil(Math.abs(delayInMinutes) / minutesInDay)
      : Math.floor(Math.abs(delayInMinutes) / minutesInDay);

    return days;
  };

  const roundToNearest5 = (x: number) => Math.round(x / 5) * 5;

  const getDelayTime = (delay: string | undefined): number => {
    if (!delay) {
      return 540;
    }
    const delayInMinutes = getDelayInMinutes(delay);
    const isBefore = isDelayBefore(delay);

    const time = !isBefore
      ? roundToNearest5(delayInMinutes % minutesInDay)
      : roundToNearest5(
          delayInMinutes > minutesInDay
            ? delayInMinutes % minutesInDay
            : minutesInDay % delayInMinutes,
        );

    const result = delayInMinutes < minutesInDay ? time : isBefore ? minutesInDay - time : time;

    return result;
  };

  const getDelayValue = (pastOrFuture: string, delayDays: number, delayTime: number): string => {
    const value =
      pastOrFuture === 'after'
        ? delayDays * minutesInDay + Math.abs(delayTime)
        : -delayDays * minutesInDay + Math.abs(delayTime);

    return `${pastOrFuture === 'before' && delayDays > 0 ? '-' : ''}${Math.abs(value)}m`;
  };

  // Annual-specific handlers
  const handleAnnualPastOrFutureChange = ({ target }: any) => {
    setPastOrFuture(target.value);
    onUpdate({
      schedule: {
        value: template.schedule?.value || 'immediately',
        visible: template.schedule?.visible ?? true,
        scheduledType: template.schedule?.scheduledType,
        time: undefined,
        delay: getDelayValue(target.value, delayDays ?? 0, delayTime),
      },
    });
  };

  const handleAnnualTimeChange = async (time: dayjs.Dayjs | null) => {
    if (time) {
      const minutesInHour = 60;
      const hours = time.get('h');
      const minutes = time.get('m');
      const timeInMinutes = hours * minutesInHour + minutes;
      setDelayTime(timeInMinutes);
      onUpdate({
        schedule: {
          value: template.schedule?.value || 'immediately',
          visible: template.schedule?.visible ?? true,
          scheduledType: template.schedule?.scheduledType,
          time: undefined,
          delay: getDelayValue(pastOrFuture, delayDays ?? 0, timeInMinutes),
        },
      });
    }
  };

  const handleAnnualDelayDaysChange = (e: any) => {
    const { value } = e.target;
    const days = Number(value);
    setDelayDays(value === '' ? undefined : days);
    onUpdate({
      schedule: {
        value: template.schedule?.value ?? 'immediately',
        visible: template.schedule?.visible ?? true,
        scheduledType: template.schedule?.scheduledType,
        time: undefined,
        delay: !value ? 'NaN' : getDelayValue(pastOrFuture, Number(value), delayTime),
      },
    });
  };

  useEffect(() => {
    if (template.schedule?.scheduledType === 'delay' && template.schedule.delay) {
      setDelayPeriod(template.schedule.delay[template.schedule.delay.length - 1]);
      setDelayValue(template.schedule.delay.replace('-', '').slice(0, -1));
    }
  }, [template.schedule]);

  // Initialize annual-specific state
  useEffect(() => {
    if (campaignType === 'annual') {
      if (template.schedule?.delay) {
        const calculatedPastOrFuture =
          !template.schedule.delay || getDelayDays(template.schedule.delay) === 0
            ? 'before'
            : isDelayBefore(template.schedule.delay)
            ? 'before'
            : 'after';
        setPastOrFuture(calculatedPastOrFuture);
        setDelayDays(template.schedule.delay ? getDelayDays(template.schedule.delay) : 0);
        setDelayTime(template.schedule.delay ? getDelayTime(template.schedule.delay) : 540);
      }
    }
  }, [campaignType, template.schedule]);

  // Clear survey visibility and survey data when campaignType is not 'message' or 'join'
  useEffect(() => {
    if (
      !forceSurvey &&
      campaignType !== 'message' &&
      campaignType !== 'join' &&
      (template.surveyVisible || template.survey)
    ) {
      onUpdate({
        surveyVisible: false,
        survey: undefined,
      });
    }
  }, [campaignType, template.surveyVisible, template.survey, forceSurvey]);

  // Poll configs: keep the survey on and visible in the data.
  useEffect(() => {
    if (forceSurvey && (!template.survey?.active || !template.surveyVisible)) {
      onUpdate({ survey: { active: true }, surveyVisible: true });
    }
  }, [forceSurvey, template.survey?.active, template.surveyVisible]);


  return (
    <SelectableCard
      ref={setNodeRef}
      style={style}
      selected={isSelected}
      sx={{ mb: 1, p: 0, opacity: isDragging ? 0.5 : 1, overflow: 'visible' }}
      onClick={onSelect}
    >
      <CardContent sx={{pb: '0 !important'}}>
        {/* Header */}
        <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
          <Box {...attributes} {...listeners} sx={{ mr: 1, cursor: 'grab' }}>
            <DragIcon />
          </Box>
          <IconButton
            size="small"
            onClick={(e: React.MouseEvent) => {
              e.stopPropagation();
              onDelete();
            }}
            sx={{ ml: 1 }}
          >
            <DeleteIcon />
          </IconButton>
        </Box>

        {/* Main Content Layout - Following ConfigurationSection pattern */}
        <Box display="flex" alignItems="flex-start">
          {/* Message Content - Left Side */}
          <Box flex={1} pr={2}>
            {/* SMS or MMS - Show MMS options with shared text field */}
            {template.messageType === 'sms-or-mms' && (
              <Box>
                <Box mb={1}>
                  {(template.mmsFieldsVisibility?.attachment ?? true) && (
                    <Box mb={1} display="flex" alignItems="center" gap={1}>
                      <Button variant="contained" size="small" component="label">
                        Attach
                        <input
                          type="file"
                          hidden
                          onChange={e => {
                            const file = e.target.files?.[0];
                            if (file) {
                              onUpdate({ attachment: file });
                            }
                          }}
                        />
                      </Button>
                      {template.attachment && (
                        <Typography variant="body2" color="text.secondary">
                          {template.attachment.name}
                        </Typography>
                      )}
                    </Box>
                  )}

                  {(template.mmsFieldsVisibility?.subject ?? true) && (
                    <TextField
                      fullWidth
                      placeholder="MMS Subject (optional)"
                      value={template.mmsSubject || ''}
                      onChange={e => onUpdate({ mmsSubject: e.target.value })}
                    />
                  )}
                </Box>
                <Box mb={1}>
                  <TemplateText
                    text={template.smsText || template.mmsText || ''}
                    updateText={(newText: string) => onUpdate({ smsText: newText, mmsText: newText })}
                    customFields={customFields}
                    messageTemplates={[]}
                    disabled={false}
                    showConfirmationText={false}
                    confirmationText=""
                    setFocus={false}
                    hideMessageTemplates={true}
                    hideToolButtons={false}
                    hideAbsentDefaultValueAlert={true}
                  />
                </Box>
              </Box>
            )}

            {/* SMS Only Configuration */}
            {template.messageType === 'sms-only' && (
              <Box mb={1}>
                <TemplateText
                  text={template.smsText || ''}
                  updateText={(newText: string) => onUpdate({ smsText: newText })}
                  customFields={customFields}
                  messageTemplates={[]}
                  disabled={false}
                  showConfirmationText={false}
                  confirmationText=""
                  setFocus={false}
                  hideMessageTemplates={true}
                  hideToolButtons={false}
                  hideAbsentDefaultValueAlert={true}
                />
              </Box>
            )}

            {template.messageType === 'mms-only' && (
             
             <Box mb={1}>
             <Box mb={1}>
                {(template.mmsFieldsVisibility?.attachment ?? true) && (
                  <Box mb={1} display="flex" alignItems="center" gap={1}>
                    <Button variant="contained" size="small" component="label">
                      Attach
                      <input
                        type="file"
                        hidden
                        onChange={e => {
                          const file = e.target.files?.[0];
                          if (file) {
                            onUpdate({ attachment: file });
                          }
                        }}
                      />
                    </Button>
                    {template.attachment && (
                      <Typography variant="body2" color="text.secondary">
                        {template.attachment.name}
                      </Typography>
                    )}
                  </Box>
                )}

                {(template.mmsFieldsVisibility?.subject ?? true) && (
                  <TextField
                    fullWidth
                    placeholder="MMS Subject (optional)"
                    value={template.mmsSubject || ''}
                    onChange={e => onUpdate({ mmsSubject: e.target.value })}
                  />
                )}
              </Box>
              <Box>
                 {(template.mmsFieldsVisibility?.text ?? true) && (
                  <TemplateText
                    text={template.mmsText || ''}
                    updateText={(newText: string) => onUpdate({ mmsText: newText })}
                    customFields={customFields}
                    messageTemplates={[]}
                    disabled={false}
                    showConfirmationText={false}
                    confirmationText=""
                    setFocus={false}
                    hideMessageTemplates={true}
                    hideToolButtons={false}
                    hideAbsentDefaultValueAlert={true}
                  />
                )}
              </Box>
              </Box>
            )}

            {/* Survey */}
            {(forceSurvey || (template.surveyVisible ?? false)) && (
              <Box mb={1}>
                <FormControlLabel
                  sx={flushControlLabelSx}
                  control={
                    <Checkbox
                      size="small"
                      checked={forceSurvey ? true : template.survey?.active ?? false}
                      disabled={forceSurvey}
                      onChange={e =>
                        onUpdate({
                          survey: e.target.checked
                            ? {
                                active: true,
                              }
                            : undefined,
                        })
                      }
                    />
                  }
                  label="Capture response to message as survey answer"
                />
              </Box>
            )}

            {/* Data Capture */}
            {(template.dataCaptureVisible ?? true) && (
              <Box mb={1}>
                <Box display="flex" alignItems="center" gap={1} mb={1}>
                  <FormControlLabel
                    sx={flushControlLabelSx}
                    control={
                      <Checkbox
                        checked={template.dataCapture?.active || false}
                        onChange={e =>
                          onUpdate({
                            dataCapture: e.target.checked
                              ? {
                                  active: true,
                                  description:
                                    template.dataCapture?.description ||
                                    'Capture response to message as',
                                }
                              : undefined,
                          })
                        }
                        size="small"
                      />
                    }
                    label="Capture response to message as"
                  />

                    <MuiFormControl sx={{ minWidth: 200, ml: 1 }}>
                      <Select
                        size="small"
                        disabled={!template.dataCapture?.active}
                        value={template.dataCapture?.description || ''}
                        onChange={e =>
                          onUpdate({
                            dataCapture: {
                              active: template.dataCapture?.active || true,
                              description: e.target.value,
                            },
                          })
                        }
                      >
                        {metadataTypes.map(type => (
                          <MenuItem key={type} value={type}>
                            {type.charAt(0).toUpperCase() + type.slice(1).replace(/_/g, ' ')}
                          </MenuItem>
                        ))}
                      </Select>
                    </MuiFormControl>
                </Box>
              </Box>
            )}

            {/* Schedule */}
            {(template.scheduleVisible ?? true) && (
              <Box mb={3}>
                {campaignType === 'annual' ? (
                  <Box>
                    <Stack direction="row" alignItems="center" mb={1}>
                      <Typography color="text.primary" mr={1.25}>Schedule:</Typography>
                      <TextField
                        sx={{
                          width: '90px',
                          marginRight: '10px',
                          marginLeft: '10px',
                          '& .MuiOutlinedInput-input': {
                            height: '35px',
                            paddingTop: 0,
                            paddingBottom: 0,
                          },
                        }}
                        id="outlined-basic"
                        placeholder="Period"
                        name="delay"
                        type="number"
                        inputProps={{ min: 0 }}
                        variant="outlined"
                        value={
                          (delayDays === 0 || delayDays) && !Number.isNaN(delayDays)
                            ? Math.abs(delayDays)
                            : ''
                        }
                        onChange={handleAnnualDelayDaysChange}
                      />
                      <Typography color="text.primary">Days</Typography>
                      <Select
                        sx={{ height: '35px', marginLeft: '10px' }}
                        MenuProps={{ disableScrollLock: true }}
                        labelId="demo-simple-select-label"
                        size="small"
                        value={Math.abs(delayDays ?? 0) === 0 ? 'before' : pastOrFuture}
                        onChange={handleAnnualPastOrFutureChange}
                      >
                        <MenuItem value={'before'} key={'before'}>
                          Before
                        </MenuItem>
                        <MenuItem value={'after'} key={'after'}>
                          After
                        </MenuItem>
                      </Select>
                      <Typography color="text.primary" ml={1.25}>at</Typography>
                      <LocalizationProvider dateAdapter={AdapterDayjs}>
                        <DemoContainer sx={pickerRowOffsetSx} components={['TimePicker']}>
                          <TimePicker
                            sx={{
                              '& .MuiStack-root': { paddingTop: 0 },
                              '& .MuiOutlinedInput-root': {
                                height: '35px',
                                width: '150px',
                                marginRight: 0,
                                marginLeft: '10px',
                              },
                              overflow: 'hidden',
                            }}
                            ampm
                            format="hh:mm A"
                            onChange={value => handleAnnualTimeChange(value)}
                            defaultValue={
                              delayTime
                                ? dayjs().startOf('d').add(Math.abs(delayTime), 'm')
                                : dayjs().startOf('d').add(Math.abs(540), 'm')
                            }
                          />
                        </DemoContainer>
                      </LocalizationProvider>
                    </Stack>
                  </Box>
                ) : (
                  <Stack direction="row" alignItems="center">
                    <Typography color="text.primary" mr={1.25}>Schedule:</Typography>
                    <Select
                      sx={{ height: '35px', marginRight: '10px', minWidth: '160px' }}
                      MenuProps={{ disableScrollLock: true }}
                      labelId="schedule-select-label"
                      size="small"
                      value={template.schedule?.scheduledType || 'immediate'}
                      onChange={handleScheduleTypeChange}
                      name="scheduledType"
                    >
                      <MenuItem value={'immediate'} key={'immediate'}>
                        Immediately
                      </MenuItem>
                      <MenuItem value={'delay'} key={'delay'}>
                        After Delay
                      </MenuItem>
                      <MenuItem value={'time'} key={'time'}>
                        Specific Date/Time
                      </MenuItem>
                      <MenuItem value={'reply'} key={'reply'}>
                        After Response
                      </MenuItem>
                    </Select>

                    {template.schedule?.scheduledType === 'delay' && (
                      <>
                        <TextField
                          sx={{
                            marginRight: '10px',
                            '& .MuiOutlinedInput-input': {
                              height: '35px',
                              paddingTop: 0,
                              paddingBottom: 0,
                              width: '80px',
                            },
                          }}
                          id="outlined-basic"
                          placeholder="Period"
                          name="delay"
                          type="number"
                          inputProps={{ min: 1 }}
                          variant="outlined"
                          value={delayValue}
                          onChange={handleDelayChange}
                        />
                        <Select
                          sx={{ height: '35px', marginRight: '10px' }}
                          MenuProps={{ disableScrollLock: true }}
                          labelId="delay-period-select-label"
                          id="delay-period-select"
                          size="small"
                          value={delayPeriod}
                          onChange={handleSchedulePeriodChange}
                        >
                          <MenuItem value={'m'} key={'minutes'}>
                            Minutes
                          </MenuItem>
                          <MenuItem value={'h'} key={'hours'}>
                            Hours
                          </MenuItem>
                          <MenuItem value={'d'} key={'days'}>
                            Days
                          </MenuItem>
                        </Select>
                      </>
                    )}

                    {template.schedule?.scheduledType === 'time' && (
                      <LocalizationProvider dateAdapter={AdapterDayjs}>
                        <DemoContainer components={['DateTimePicker']} sx={pickerRowOffsetSx}>
                          <DateTimePicker
                            sx={{
                              overflow: 'hidden',
                              '& .MuiOutlinedInput-root': { height: '35px' },
                              '& .MuiInputLabel-formControl': { top: '-2px' },
                            }}
                            ampm
                            format="MM/DD/YYYY hh:mm A"
                            disablePast
                            closeOnSelect={false}
                            onChange={(value: any) => handleDateTimeSave(value)}
                            defaultValue={template.schedule.time ? dayjs(template.schedule.time) : null}
                          />
                        </DemoContainer>
                      </LocalizationProvider>
                    )}
                  </Stack>
                )}
              </Box>
            )}
          </Box>

          {/* Visual Divider */}
          <Box width="1px" bgcolor="grey.300" mx={2} mb={1} alignSelf="stretch" />

          {/* Properties Panel - Right Side (Following ConfigurationSection pattern) */}
          <Stack gap={2} p={2} borderRadius={1} flexShrink={0} minWidth={210}>

            {/* Template Details */}
            <Box>
              <Typography variant="subtitle2" mb={1} color="grey.600">
                Message Type
              </Typography>
              <TextField
                fullWidth
                value={template.messageType}
                select
                onChange={e =>
                  onUpdate({
                    messageType: e.target.value as any,
                  })
                }
              >
                <MenuItem value="sms-only">SMS Only</MenuItem>
                <MenuItem value="mms-only">MMS Only</MenuItem>
                <MenuItem value="sms-or-mms">SMS or MMS</MenuItem>
              </TextField>
            </Box>

            {/* Field Visibility Controls */}
            <Box>
              <Box>
                <Box display="flex" flexDirection="column" gap={0.5}>
                  {/* MMS Field Visibility */}
                  {(template.messageType === 'mms-only' ||
                    template.messageType === 'sms-or-mms') && (
                    <>
                      <FormControlLabel
                        control={
                          <Checkbox
                            size="small"
                            checked={
                              template.mmsFieldsVisibility?.subject ??
                              true
                            }
                            onChange={e =>
                              onUpdate({
                                mmsFieldsVisibility: {
                                  ...template.mmsFieldsVisibility,
                                  subject: e.target.checked,
                                },
                              })
                            }
                          />
                        }
                        label={<Typography variant="body2">Subject</Typography>}
                      />
                      {template.messageType === 'mms-only' && (
                        <FormControlLabel
                          control={
                            <Checkbox
                              size="small"
                              checked={
                                template.mmsFieldsVisibility?.text ??
                                true
                              }
                              onChange={e =>
                                onUpdate({
                                  mmsFieldsVisibility: {
                                    ...template.mmsFieldsVisibility,
                                    text: e.target.checked,
                                  },
                                })
                              }
                            />
                          }
                          label={<Typography variant="body2">Message Text</Typography>}
                        />
                      )}
                    </>
                  )}

                  {/* Survey Visibility */}
                  {(forceSurvey ||
                    campaignType === 'message' ||
                    campaignType === 'join' ||
                    campaignType === 'enrollment') && (
                    <FormControlLabel
                      control={
                        <Checkbox
                          size="small"
                          checked={forceSurvey ? true : template.surveyVisible ?? false}
                          disabled={forceSurvey}
                          onChange={e =>
                            onUpdate({
                              surveyVisible: e.target.checked,
                            })
                          }
                        />
                      }
                      label={<Typography variant="body2">Poll/Survey</Typography>}
                    />
                  )}

                  {/* Data Capture Visibility */}
                  <FormControlLabel
                    control={
                      <Checkbox
                        size="small"
                        checked={template.dataCaptureVisible ?? true}
                        onChange={e =>
                          onUpdate({
                            dataCaptureVisible: e.target.checked,
                          })
                        }
                      />
                    }
                    label={<Typography variant="body2">Data Capture</Typography>}
                  />

                  {/* Schedule Visibility */}
                  <FormControlLabel
                    control={
                      <Checkbox
                        size="small"
                        checked={template.scheduleVisible ?? true}
                        onChange={e =>
                          onUpdate({
                            scheduleVisible: e.target.checked,
                          })
                        }
                      />
                    }
                    label={<Typography variant="body2">Schedule</Typography>}
                  />

                </Box>
              </Box>
            </Box>
          </Stack>
        </Box>

        {/* Disable New Messages Toggle (hidden for poll — always a single message) */}
        {!forceSurvey && (
        <Box display="flex" justifyContent="center" mt={2} mb={1} alignItems="center">
          {!(template.oneMessageOnly ?? false) && (
            <SquareIconButton color="primary" style={{ marginRight: 1 }}>
              <AddCircleOutlineIcon style={{ transform: 'scale(0.8)' }} />
            </SquareIconButton>
          )}
          <FormControlLabel
            control={
              <Checkbox
                checked={template.oneMessageOnly ?? false}
                onChange={e =>
                  onUpdate({
                    oneMessageOnly: e.target.checked,
                  })
                }
                size="small"
              />
            }
            label={<Typography variant="body2">Disable New Messages</Typography>}
          />
        </Box>
        )}
      </CardContent>
    </SelectableCard>
  );
};
